import React, { useEffect, useState, useCallback, useMemo } from "react";
import CommonDataTable from "../../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../../components/AppToast";
import { formatToDisplayDate } from "../../../utils/dateFormatter";
import {
  columnLabels,
  defaultVisibleFields,
  getColumnLabel,
} from "./constants/orderListColumns";
import { loadOrdersFromStorage } from "./orderStorage";

const startCase = (str) =>
  (str || "")
    .replace(/([A-Z]+)/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/gi, "ID")
    .trim();

export default function OrderList({
  onViewOrder,
  onEditOrder,
  onCreateClick,
  refreshKey = 0,
}) {
  const [rows, setRows] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleOpenMenu = useCallback((e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  }, []);

  const allPossibleColumns = useMemo(() => {
    const listFields = defaultVisibleFields.filter((f) => f !== "__actions");
    const actionsColumn = {
      field: "__actions",
      headerName: columnLabels.__actions || "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    };
    return [
      actionsColumn,
      ...listFields.map((f) => {
        const col = {
          field: f,
          headerName: getColumnLabel(f, startCase),
        };
        if (f === "orderDate") {
          col.valueFormatter = (params) => {
            const v = params?.value;
            if (!v) return "";
            const formatted = formatToDisplayDate(v, false, "24h");
            return formatted || String(v);
          };
        }
        return col;
      }),
    ];
  }, [handleOpenMenu]);

  const loadList = useCallback(() => {
    setLoading(true);
    try {
      const raw = loadOrdersFromStorage();
      const builtRows = raw.map((rec, idx) => ({
        ...rec,
        id: rec._id || rec.omsOrderId || `row-${idx}`,
      }));

      setRows(builtRows);
    } catch (err) {
      toast.error(err?.message || "Failed to load orders");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList, refreshKey]);

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = () => {
    onViewOrder?.(menuRow);
    handleCloseMenu();
  };

  const handleEdit = () => {
    onEditOrder?.(menuRow);
    handleCloseMenu();
  };

  return (
    <>
      {loading && <div className="text-center py-3">Loading orders…</div>}

      <CommonDataTable
        id="oms-order-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={defaultVisibleFields}
        initialRows={rows}
        height={500}
        rowsPerPageOptions={[10, 25, 50, 100]}
        showCreateButton
        createButtonText="Create New Order"
        createButtonIcon="bi-plus-lg"
        onCreateClick={onCreateClick}
        enableExportButton={false}
        enableImportButton={false}
        page={page}
        pageSize={pageSize}
        rowCount={rows.length}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        onManualRefresh={async () => {
          loadList();
        }}
        forInventory
        onDataRefresh={async () => {
          loadList();
        }}
      />

      <Menu open={Boolean(menuAnchorEl)} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={handleView}>
          <i className="bi bi-eye me-2"></i>
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <i className="bi bi-pencil me-2"></i>
          Edit
        </MenuItem>
      </Menu>
    </>
  );
}
